// src/hooks/useCart.ts
//
// Manages the anonymous cart session (session_id + cart_id in localStorage) and
// calls the two proven, deployed SmartShop cart endpoints. No login required —
// matches the design decision: fast "Add to Cart" for anyone, login only binds
// the cart to a real account later (at checkout or earlier, customer's choice).
import { useState, useCallback } from 'react';
import { SMARTSHOP_API } from '../firebaseConfig';

const SESSION_KEY = 'smartshop_session_id';
const CART_KEY = 'smartshop_cart_id';

export interface CartItem {
  product_id: string;
  variant_id: string | null;
  qty: number;
  price_snapshot: number;
  name_snapshot: string;
  price_type: 'fixed' | 'variable' | 'enquire';
  variable_amount: number | null;
  image_snapshot?: string | null;          // preview image (variant image or product primary)
  variant_name_snapshot?: string | null;   // e.g. "Blue" — shown as "Name - Variant"
  slug?: string | null;                    // for the product link (/product/{slug})
}

export interface CartState {
    cart_id: string | null;
    session_id: string | null;
    items: CartItem[];
    total: number;
    item_count: number;
    merge_notice: boolean;
  }

function getStoredIds() {
  return {
    session_id: localStorage.getItem(SESSION_KEY),
    cart_id: localStorage.getItem(CART_KEY),
  };
}

function storeIds(session_id: string | null, cart_id: string | null) {
  if (session_id) localStorage.setItem(SESSION_KEY, session_id);
  if (cart_id) localStorage.setItem(CART_KEY, cart_id);
}

export function useCart() {
  const [cart, setCart] = useState<CartState | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addToCart = useCallback(async (
    productId: string,
    opts?: { qty?: number; variantId?: string; variableAmount?: number }
  ) => {
    setAdding(true);
    setError(null);
    try {
      const { session_id, cart_id } = getStoredIds();
      const res = await fetch(`${SMARTSHOP_API}/smartshop-cart-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id, cart_id, product_id: productId,
          qty: opts?.qty ?? 1, variant_id: opts?.variantId,
          variable_amount: opts?.variableAmount,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `add failed (${res.status})`);
      storeIds(data.session_id, data.cart_id);
      setCart(data);
      window.dispatchEvent(new Event('cart:changed'));
      return data;
    } catch (e: any) {
      setError(e.message);
      throw e;
    } finally {
      setAdding(false);
    }
  }, []);

  const loadCart = useCallback(async () => {
    try {
      const { session_id, cart_id } = getStoredIds();
      if (!session_id && !cart_id) {
        setCart({ cart_id: null, session_id: null, items: [], total: 0, item_count: 0, merge_notice: false });
        return;
      }
      const res = await fetch(`${SMARTSHOP_API}/smartshop-cart-get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id, cart_id }),
      });
      const data = await res.json();
      if (data.cart_id) storeIds(data.session_id, data.cart_id);
      setCart(data);
    } catch (e: any) {
      setError(e.message);
    }
  }, []);

  // Premium cart mutation — one call covers both operations:
  //   qty >= 1  -> smartshop-cart-update updates the line
  //   qty === 0 -> removes the line
  // Backend (cart.update_qty) RAISES "item not in cart" if the line is already gone
  // (e.g. a second tab removed it). We treat that specific 400 as "someone else already
  // changed this cart" and silently re-sync rather than showing the customer an error.
  const updateItem = useCallback(async (
    productId: string,
    qty: number,
    variantId?: string
  ) => {
    setError(null);
    try {
      const { session_id, cart_id } = getStoredIds();
      const res = await fetch(`${SMARTSHOP_API}/smartshop-cart-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id, cart_id, product_id: productId,
          qty, variant_id: variantId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // raced/stale line — re-sync from source instead of surfacing a scary error
        if (res.status === 400) {
          await loadCart();
          return;
        }
        throw new Error(data.error || `update failed (${res.status})`);
      }
      if (data.cart_id) storeIds(data.session_id, data.cart_id);
      setCart(data);
      window.dispatchEvent(new Event('cart:changed'));
      return data;
    } catch (e: any) {
      setError(e.message);
      throw e;
    }
  }, [loadCart]);

  return { cart, adding, error, addToCart, loadCart, updateItem };
}